// 한도 경고 띠(대표만) — 80% 는 닫으면 이번 달 숨김 · 100% 는 닫아도 다음 날 다시 · 막지 않는다 · 문구는 core 한 벌.

import { X } from "lucide-react";
import { useSyncExternalStore } from "react";

import { dateAfterDays } from "@buildpy/core/lib/format";
import type { PlanUsage } from "@buildpy/core/modules/plan/usage-limit";
import Banner from "@buildpy/ui/banner";
import { Button } from "@buildpy/ui/button";
import { Link } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { PLAN_INTEREST_PATH, PLAN_SETTINGS_PATH } from "@/modules/plan/copy";
import { type KeyStore, rememberUsageDismiss, usageAlertsOf } from "./usage-banner-state";

const browserStorage = (): KeyStore | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readDismissed = (store: KeyStore | null, key: string): boolean => {
  if (!store) return false;
  try {
    return store.getItem(key) === "1";
  } catch {
    return false;
  }
};

const listeners = new Set<() => void>();
const sessionDismissed = new Set<string>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

const dismiss = (key: string) => {
  sessionDismissed.add(key);
  rememberUsageDismiss(browserStorage(), key);
  listeners.forEach((l) => l());
};

const LINK = "font-semibold underline underline-offset-4";

interface Props {
  usage: PlanUsage;
  className?: string;
  // 테스트용 — 비우면 오늘(KST).
  today?: string;
}

const UsageBanner = ({ usage, className, today = dateAfterDays(0) }: Props) => {
  const alerts = usageAlertsOf(usage, today);
  const keys = alerts.map((a) => a.key);
  // 문자열 스냅샷 — 서버 렌더(닫은 것 없음)와 브라우저 값이 달라도 수화 뒤 맞춘다.
  const snapshot = useSyncExternalStore(
    subscribe,
    () => keys.filter((key) => sessionDismissed.has(key) || readDismissed(browserStorage(), key)).join("\n"),
    () => ""
  );
  const hidden = new Set(snapshot === "" ? [] : snapshot.split("\n"));
  const visible = alerts.filter((a) => !hidden.has(a.key));
  if (visible.length === 0) return null;
  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid="usage-banner">
      {visible.map((a) => (
        <Banner key={a.key} tone="warning">
          <span className="flex items-start gap-2" data-level={a.level} data-testid={`usage-banner-${a.kind}`}>
            <span className="min-w-0 flex-1 break-words">
              {a.message}{" "}
              {a.level === "over" && (
                <>
                  <Link className={LINK} href={PLAN_INTEREST_PATH}>
                    출시 알림 받기
                  </Link>
                  {" · "}
                </>
              )}
              <Link className={LINK} href={PLAN_SETTINGS_PATH}>
                사용량 보기
              </Link>
            </span>
            <Button aria-label="경고 닫기" data-testid={`usage-banner-dismiss-${a.kind}`} size="icon-xs" variant="ghost" onClick={() => dismiss(a.key)}>
              <X />
            </Button>
          </span>
        </Banner>
      ))}
    </div>
  );
};

export default UsageBanner;
